import type { Event, PublicState } from '@/api/generated/game';
import {
  ClientCommand,
  ClientHello,
  ClientLeave,
  ClientPing,
  ContractVersion,
  ErrCodeBadContract,
  ErrCodeNoRoom,
  ErrCodeUnauthorized,
  ServerClosed,
  ServerError,
  ServerEvents,
  ServerHelloOK,
  ServerState,
  type ClosedPayload,
  type ErrorPayload,
  type EventsPayload,
  type HelloOKPayload,
  type ServerEnvelope,
  type StatePayload,
} from '@/api/generated/ws-protocol';

import { browserSocket, type SocketFactory, type SocketLike } from './socket';
import {
  TransportOfflineError,
  type ConnectOpts,
  type ConnectionStatus,
  type GameTransport,
  type Intent,
  type TransportEvent,
} from './types';

export interface WsTransportOptions {
  socketFactory?: SocketFactory;
  /**
   * Задержки переподключения, мс. Телефон на вечеринке блокируется каждые
   * полминуты, поэтому первая попытка почти мгновенная, а потолок низкий:
   * ждать полминуты после разблокировки экрана никто не будет.
   */
  backoffMs?: number[];
  /** Прикладной ping: подтверждает живость горутины комнаты, а не только сокета. */
  pingIntervalMs?: number;
  /** Доля дрожания задержки, чтобы восемь телефонов не ломились разом. */
  jitter?: () => number;
}

const DEFAULT_BACKOFF = [300, 1_000, 2_500, 5_000, 10_000, 15_000];
const DEFAULT_PING_MS = 20_000;

/**
 * WsTransport — единственная реализация GameTransport.
 *
 * Переподключение здесь не улучшение, а требование: Telegram выгружает WebView
 * агрессивно, экран блокируется постоянно, деплой случается посреди партии.
 * Отдельного протокола досылки нет и не нужно — hello_ok всегда несёт полное
 * состояние, поэтому восстановление сводится к повтору хендшейка.
 *
 * Намерения в офлайне не копятся: send() бросает. За время обрыва ход мог уйти
 * дальше, и отправленная пачкой «Выполнил» приехала бы в чужой ход.
 */
export class WsTransport implements GameTransport {
  private readonly newSocket: SocketFactory;
  private readonly backoff: number[];
  private readonly pingIntervalMs: number;
  private readonly jitter: () => number;

  private opts: ConnectOpts | null = null;
  private socket: SocketLike | null = null;
  private status: ConnectionStatus = 'idle';
  private attempt = 0;
  private seq = 0;
  private stopped = false;
  private helloId: string | null = null;
  /** Сессию за одну попытку перевыпускаем один раз: второй 401 подряд не лечится. */
  private refreshedThisAttempt = false;
  private lastInboundAt = 0;

  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;

  private readonly subscribers = new Set<(event: TransportEvent) => void>();
  private ready: {
    resolve: () => void;
    reject: (err: Error) => void;
    settled: boolean;
  } | null = null;

  constructor(options: WsTransportOptions = {}) {
    this.newSocket = options.socketFactory ?? browserSocket;
    this.backoff = options.backoffMs ?? DEFAULT_BACKOFF;
    this.pingIntervalMs = options.pingIntervalMs ?? DEFAULT_PING_MS;
    this.jitter = options.jitter ?? Math.random;
  }

  connect(opts: ConnectOpts): Promise<void> {
    this.disconnect();
    this.opts = opts;
    this.stopped = false;
    this.attempt = 0;

    const promise = new Promise<void>((resolve, reject) => {
      this.ready = { resolve, reject, settled: false };
    });
    void this.open();
    return promise;
  }

  send(intent: Intent): string {
    if (this.status !== 'online' || !this.socket) throw new TransportOfflineError();
    const id = this.nextId();
    this.write({ type: ClientCommand, id, payload: { command: intent } });
    return id;
  }

  leave(): void {
    if (this.status !== 'online' || !this.socket) throw new TransportOfflineError();
    this.write({ type: ClientLeave, id: this.nextId() });
  }

  subscribe(fn: (event: TransportEvent) => void): () => void {
    this.subscribers.add(fn);
    return () => {
      this.subscribers.delete(fn);
    };
  }

  disconnect(): void {
    this.stopped = true;
    this.clearTimers();
    this.dropSocket();
    this.settle(new Error('соединение закрыто клиентом'));
    this.opts = null;
    if (this.status !== 'idle') this.setStatus('idle');
  }

  // ── соединение ────────────────────────────────────────────────────────────

  private async open(): Promise<void> {
    const opts = this.opts;
    if (!opts || this.stopped) return;

    this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');

    let token: string;
    try {
      token = await opts.getToken({ refresh: this.refreshedThisAttempt });
    } catch {
      // Сессию не выпустили — это не повод сдаться: 503 на /auth/telegram
      // лечится повтором, а разлогинивать по нему нельзя.
      this.scheduleRetry();
      return;
    }
    if (this.stopped || this.opts !== opts) return;

    const socket = this.newSocket(opts.url);
    this.socket = socket;

    socket.onopen = () => {
      this.lastInboundAt = Date.now();
      this.helloId = this.nextId();
      this.write({
        type: ClientHello,
        id: this.helloId,
        payload: {
          contractVersion: ContractVersion,
          token,
          roomCode: opts.roomCode,
        },
      });
      this.startPing();
    };
    socket.onmessage = (data) => this.receive(data);
    socket.onerror = () => socket.close();
    socket.onclose = () => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.clearTimers();
      if (this.stopped || this.status === 'closed' || this.status === 'fatal') return;
      this.scheduleRetry();
    };
  }

  private scheduleRetry(): void {
    if (this.stopped) return;
    const step = Math.min(this.attempt, this.backoff.length - 1);
    const base = this.backoff[step] ?? 15_000;
    const delay = Math.round(base * (0.8 + this.jitter() * 0.4));
    this.attempt += 1;
    this.setStatus('reconnecting');
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.open();
    }, delay);
  }

  private startPing(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = setInterval(() => {
      // Тишина дольше двух периодов означает мёртвую комнату при живом сокете:
      // TCP-соединение это состояние не замечает, а игрок замечает сразу.
      if (Date.now() - this.lastInboundAt > this.pingIntervalMs * 2) {
        this.dropSocket();
        this.scheduleRetry();
        return;
      }
      this.write({ type: ClientPing, id: this.nextId() });
    }, this.pingIntervalMs);
  }

  // ── приём ─────────────────────────────────────────────────────────────────

  private receive(raw: string): void {
    this.lastInboundAt = Date.now();

    let envelope: ServerEnvelope;
    try {
      envelope = JSON.parse(raw) as ServerEnvelope;
    } catch {
      return; // мусор в кадре — не повод рвать партию
    }

    switch (envelope.type) {
      case ServerHelloOK: {
        const p = envelope.payload as HelloOKPayload;
        this.attempt = 0;
        this.refreshedThisAttempt = false;
        this.setStatus('online');
        this.emit({
          kind: 'hello',
          roomCode: p.roomCode,
          roomMode: p.roomMode,
          seats: p.seats,
          state: p.state,
        });
        this.settle(null);
        return;
      }
      case ServerState:
        this.emit({ kind: 'state', state: (envelope.payload as StatePayload).state });
        return;
      case ServerEvents:
        this.emit({ kind: 'events', events: (envelope.payload as EventsPayload).events });
        return;
      case ServerError:
        this.handleError(envelope);
        return;
      case ServerClosed: {
        const reason = (envelope.payload as ClosedPayload).reason;
        this.setStatus('closed');
        this.emit({ kind: 'closed', reason });
        this.stopped = true;
        this.clearTimers();
        this.dropSocket();
        this.settle(new Error(reason));
        return;
      }
      default:
        return; // pong и всё, что добавят позже
    }
  }

  private handleError(envelope: ServerEnvelope): void {
    const { code, message } = envelope.payload as ErrorPayload;
    const inHandshake = envelope.replyTo != null && envelope.replyTo === this.helloId;

    this.emit({ kind: 'error', code, message, replyTo: envelope.replyTo });

    if (code === ErrCodeBadContract || code === ErrCodeNoRoom) {
      // Ни то, ни другое не лечится повтором: версия клиента не изменится
      // сама, а комнаты уже нет.
      this.fail(message);
      return;
    }

    if (code === ErrCodeUnauthorized) {
      if (this.refreshedThisAttempt) {
        // Второй unauthorized подряд: initData протухла, чинить нечем.
        this.fail(message);
        return;
      }
      this.refreshedThisAttempt = true;
      this.attempt = 0;
      this.dropSocket();
      this.scheduleRetry();
      return;
    }

    // bad_command, not_allowed, internal — отказ на конкретное нажатие.
    // Соединение живое, партия идёт дальше.
    if (inHandshake) this.fail(message);
  }

  private fail(message: string): void {
    this.stopped = true;
    this.clearTimers();
    this.setStatus('fatal');
    this.dropSocket();
    this.settle(new Error(message));
  }

  // ── мелочи ────────────────────────────────────────────────────────────────

  private write(payload: unknown): void {
    this.socket?.send(JSON.stringify(payload));
  }

  private nextId(): string {
    this.seq += 1;
    return String(this.seq);
  }

  private setStatus(status: ConnectionStatus): void {
    this.status = status;
    this.emit({ kind: 'status', status, attempt: this.attempt });
  }

  private emit(event: TransportEvent): void {
    for (const fn of [...this.subscribers]) fn(event);
  }

  private settle(err: Error | null): void {
    const ready = this.ready;
    if (!ready || ready.settled) return;
    ready.settled = true;
    if (err) ready.reject(err);
    else ready.resolve();
  }

  private dropSocket(): void {
    const socket = this.socket;
    if (!socket) return;
    this.socket = null;
    socket.onopen = null;
    socket.onmessage = null;
    socket.onclose = null;
    socket.onerror = null;
    socket.close();
  }

  private clearTimers(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }
}

/** Реэкспорт для тестов экранов: подменяется целиком, без правок компонентов. */
export type { PublicState, Event };
